import React, { useState, useRef, useEffect, useLayoutEffect } from "react"
import PropTypes from "prop-types"
import * as am5 from "@amcharts/amcharts5"
import * as am5wc from "@amcharts/amcharts5/wc"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"
import * as am5exporting from "@amcharts/amcharts5/plugins/exporting"
import * as am5percent from "@amcharts/amcharts5/percent"
import { formatValueToPercentOrDelta } from "Components/chartsLibrary/EvolutionWidget/index.functions.js"
import { getRandomId } from "Components/chartsLibrary/HeatMap/functions"
import { formatNumberDisplayed } from "Components/chartsLibrary/utils/global.js"

const defaultLabel = "defaultLabel5699853645672345634"

const FillingGauge = ({
  idChart,
  width,
  height,
  location,
  valueData,
  categoryData,
  dataChart,
  orientation,
  dependsOnField,
}) => {
  const [idChartState, setIdChartState] = useState(idChart ? idChart : getRandomId())
  const chartRef = useRef(null)
  const rootRef = useRef(null)

  useLayoutEffect(() => {
    initChart()

    return () => {
      if (rootRef.current !== null) {
        rootRef.current.dispose()
      }
      if (chartRef.current !== null) {
        chartRef.current.dispose()
      }
    }
  }, [])

  const disposeSeries = chart => {
    while (chart?.series.length >= 1) {
      chart?.series.removeIndex(0).dispose()
    }
  }

  useLayoutEffect(() => {
    if (chartRef.current !== null && rootRef.current !== null) {
      disposeSeries(chartRef.current)
      createSerie(rootRef.current, chartRef.current)
    }
  }, [dataChart, valueData, categoryData])

  const initChart = () => {
    let root = am5.Root.new(idChartState)
    root._logo.dispose()

    root.setThemes([am5themes_Animated.new(root)])

    let chart = root.container.children.push(
      am5percent.SlicedChart.new(root, {
        layout: root.verticalLayout,
      })
    )

    createSerie(root, chart)

    chartRef.current = chart
    rootRef.current = root

    chart.appear(200, 3000)
  }

  const createSerie = (root, chart) => {
    let series = chart.series.push(
      am5percent.PictorialStackedSeries.new(root, {
        alignLabels: false,
        orientation: orientation,
        valueField: valueData[0],
        categoryField: categoryData,
        marginLeft: 0,
        marginRight: 0,
        marginTop: 0,
        marginBottom: 0,
        svgPath:
          "M209.2 233.4l-108-31.6C88.7 198.2 80 186.5 80 173.5c0-16.3 13.2-29.5 29.5-29.5h66.3c12.2 0 24.2 3.7 34.2 10.5 6.1 4.1 14.3 3.1 19.5-2l34.8-34c7.1-6.9 6.1-18.4-1.8-24.5C238 74.8 207.4 64.1 176 64V16c0-8.8-7.2-16-16-16h-32c-8.8 0-16 7.2-16 16v48h-2.5C45.8 64-5.4 118.7.5 183.6c4.2 46.1 39.4 83.6 83.8 96.6l102.5 30c12.5 3.7 21.2 15.3 21.2 28.3 0 16.3-13.2 29.5-29.5 29.5h-66.3C100 368 88 364.3 78 357.5c-6.1-4.1-14.3-3.1-19.5 2l-34.8 34c-7.1 6.9-6.1 18.4 1.8 24.5 24.5 19.2 55.1 29.9 86.5 30v48c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16v-48.2c46.6-.9 90.3-28.6 105.7-72.7 21.5-61.6-14.6-124.8-72.5-141.7z",
        // svgPath: "M511.82,329.991c-0.256-1.212-1.064-2.244-2.192-2.784l-24.396-11.684c17.688-29.776,11.804-68.912-15.58-91.88 c-53.756-45.084-131.696-70.936-213.828-70.936c-82.128,0-160.068,25.856-213.82,70.936c-27.416,22.992-33.28,62.18-15.524,91.972 L2.276,327.203c-1.128,0.54-1.936,1.572-2.192,2.792c-0.256,1.22,0.08,2.496,0.896,3.436l21.204,24.388 c0.764,0.88,1.868,1.376,3.02,1.376c0.084,0,0.172,0,0.26-0.008c1.244-0.084,2.384-0.74,3.072-1.776l14.852-22.376 c12.648,10.112,28.392,15.776,44.916,15.776c16.872,0,33.284-5.98,46.232-16.836c27.828-23.34,73.172-37.272,121.288-37.272 c48.12,0,93.464,13.932,121.296,37.272c12.944,10.856,29.36,16.836,46.228,16.836c16.596,0,32.4-5.724,45.08-15.916l14.94,22.512 c0.692,1.04,1.824,1.696,3.076,1.776c0.084,0.008,0.172,0.008,0.256,0.008c1.156,0,2.256-0.496,3.02-1.376l21.2-24.388C511.74,332.487,512.068,331.211,511.82,329.991z"
        // svgPath: "M53.5,476c0,14,6.833,21,20.5,21s20.5-7,20.5-21V287h21v189c0,14,6.834,21,20.5,21 c13.667,0,20.5-7,20.5-21V154h10v116c0,7.334,2.5,12.667,7.5,16s10.167,3.333,15.5,0s8-8.667,8-16V145c0-13.334-4.5-23.667-13.5-31 s-21.5-11-37.5-11h-82c-15.333,0-27.833,3.333-37.5,10s-14.5,17-14.5,31v133c0,6,2.667,10.333,8,13s10.5,2.667,15.5,0s7.5-7,7.5-13 V154h10V476 M61.5,42.5c0,11.667,4.167,21.667,12.5,30S92.333,85,104,85s21.667-4.167,30-12.5S146.5,54,146.5,42 c0-11.335-4.167-21.168-12.5-29.5C125.667,4.167,115.667,0,104,0S82.333,4.167,74,12.5S61.5,30.833,61.5,42.5z"
      })
    )

    series.labels.template.setAll({
      fontSize: 20,
      fill: am5.color(0x550000),
      text: "{value}",
    })

    const greyColor = "#CCCCCC"
    const blueColor = "#67B7DC"

    series.get("colors").set("colors", [am5.color(greyColor), am5.color(blueColor)])

    series.labels.template.adapters.add("text", function (text, target, key) {
      if (target?.dataItem?.dataContext?.category === defaultLabel) return ""
      return text + "%"
    })

    series.slices.template.adapters.add("tooltipText", function (tooltipText, target) {
      var tooltipDataValue = target.dataItem.dataContext.value
      if (target?.dataItem?.dataContext?.category === defaultLabel) return `${tooltipDataValue}%`
      return tooltipText
    })

    series.data.setAll(dataChart)
    chart.appear(100, 1000)
  }

  return <div id={idChartState} style={{ width: width, height: height }} />
}

FillingGauge.propTypes = {
  width: PropTypes.string,
  height: PropTypes.string,
  location: PropTypes.number,
  dataChart: PropTypes.arrayOf(PropTypes.object),
  valueData: PropTypes.array,
  categoryData: PropTypes.string,
  orientation: PropTypes.string,
}

FillingGauge.defaultProps = {
  width: "100%",
  height: "100%",
  location: 0.3,
  dataChart: [],
  valueData: [],
  categoryData: "",
  orientation: "vertical",
}

export default FillingGauge
